import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Info, Settings, Building2, Package, GripVertical, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../services/api';

const INITIAL_STAGE = {
  name: '',
  sna: '',
  fto: '',
  cement: '',
  steel: '',
  wages: ''
};

// Initial scheme state
const INITIAL_SCHEME = {
  name: '',
  year: '',
  area: '',
  stages: []
};

// StageForm component
const StageForm = ({ stage, onSubmit, onCancel, isEditing = false, onStageChange }) => {
  const [localStage, setLocalStage] = useState(stage);

  useEffect(() => {
    setLocalStage(stage);
  }, [stage]);

  const handleChange = useCallback((field, value) => {
    setLocalStage(prev => ({ ...prev, [field]: value }));
    setTimeout(() => onStageChange(field, value), 0);
  }, [onStageChange]);

  const handleNumericChange = useCallback((field, inputValue) => {
    const value = inputValue.replace(/[^0-9]/g, '');
    handleChange(field, value);
  }, [handleChange]);

  return (
    <div className="space-y-4">
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  <div>
    <label className="text-sm font-medium text-gray-700 mb-1 block">Stage Name *</label>
    <input
      type="text"
      value={localStage.name || ''}
      onChange={(e) => handleChange('name', e.target.value)}
      placeholder="e.g., Basement Construction"
      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
    />
  </div>
 <div>
  <label className="text-sm font-medium text-gray-700 mb-1 block">SNA AMOUNT</label>
  <input
    type="text"
    inputMode="numeric"
    value={localStage.sna || ''}
    onChange={(e) => handleNumericChange('sna', e.target.value)}
    placeholder="50000"
    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
  />
</div>
  <div>
    <label className="text-sm font-medium text-gray-700 mb-1 block">FTO AMOUNT</label>
    <input
      type="text"
      inputMode="numeric"
      value={localStage.fto || ''}
      onChange={(e) => handleNumericChange('fto', e.target.value)}
      placeholder="30000"
      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
    />
  </div>
</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Cement (bags)</label>
          <input
            type="text"
            value={localStage.cement || ''}
            onChange={(e) => handleChange('cement', e.target.value)}
            placeholder="30"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Steel</label>
          <input
            type="text"
            value={localStage.steel || ''}
            onChange={(e) => handleChange('steel', e.target.value)}
            placeholder="320 Kg.(8mm - 100Kg.10mm - 220 Kg.)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Wages</label>
          <input
            type="text"
            value={localStage.wages || ''}
            onChange={(e) => handleChange('wages', e.target.value)}
            placeholder="35"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={() => onSubmit(localStage)}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          {isEditing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {isEditing ? 'Save Changes' : 'Add Stage'}
        </button>
      </div>
    </div>
  );
};

const MastersManagement = () => {
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [schemeDetails, setSchemeDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  const [newSchemeData, setNewSchemeData] = useState(INITIAL_SCHEME);
  const [newStage, setNewStage] = useState(INITIAL_STAGE);

  // Drag and drop state
  const [draggedStage, setDraggedStage] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // API helpers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const handleApiError = useCallback((err, defaultMessage) => {
    console.error('API Error:', err);
    setError(defaultMessage);
  }, []);

  // Message handlers
  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  const showSuccess = useCallback((message) => {
    setSuccess(message);
    setError('');
  }, []);

  const showError = useCallback((message) => {
    setError(message);
    setSuccess('');
  }, []);

  // Fetch schemes
  const fetchSchemes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/master/scheme`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();
      if (response.ok && data.data) {
        setSchemes(data.data);
      } else {
        setSchemes([]);
        showError(data.message || 'Failed to fetch schemes');
      }
    } catch (err) {
      setSchemes([]);
      handleApiError(err, 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, showError, handleApiError]);

  // Initialize component
  useEffect(() => {
    fetchSchemes();
  }, [fetchSchemes]);

  // Fetch scheme details
  const fetchSchemeDetails = useCallback(async (schemeName) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/master/scheme/${schemeName}`, {
        headers: getAuthHeaders()
      });

      const data = await response.json();
      if (response.ok && data.data) {
        // Sort stages by order if available
        if (data.data.detail && Array.isArray(data.data.detail)) {
          data.data.detail.sort((a, b) => (a.order || 0) - (b.order || 0));
        }
        setSchemeDetails(data.data);
      } else {
        showError(data.message || 'Failed to fetch scheme details');
        setSchemeDetails(null);
      }
    } catch (err) {
      handleApiError(err, 'Network error while fetching scheme details.');
      setSchemeDetails(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, showError, handleApiError]);

  // Export all schemes and their stages to an Excel (.xlsx) file
  const handleExportToExcel = useCallback(async () => {
    if (!schemes || schemes.length === 0) {
      showError('No schemes available to export');
      return;
    }

    setExporting(true);
    clearMessages();

    try {
      const rows = [];

      for (const scheme of schemes) {
        let stages = [];
        try {
          const response = await fetch(`${API_BASE_URL}/master/scheme/${scheme.name}`, {
            headers: getAuthHeaders()
          });
          const data = await response.json();
          if (response.ok && data.data && Array.isArray(data.data.detail)) {
            stages = [...data.data.detail].sort((a, b) => (a.order || 0) - (b.order || 0));
          }
        } catch (err) {
          // If a single scheme's details fail, still export the rest
          console.error(`Failed to fetch details for scheme ${scheme.name}:`, err);
        }

        if (stages.length === 0) {
          rows.push({
            'Scheme Name': scheme.name || '',
            'Financial Year': scheme.year || '',
            'Allotted House Size (sq ft)': scheme.area || '',
            'Stage Order': '',
            'Stage Name': '',
            'SNA Amount': '',
            'FTO Amount': '',
            'Cement (bags)': '',
            'Steel': '',
            'Wages': ''
          });
        } else {
          stages.forEach((stage, index) => {
            rows.push({
              'Scheme Name': scheme.name || '',
              'Financial Year': scheme.year || '',
              'Allotted House Size (sq ft)': scheme.area || '',
              'Stage Order': stage.order || index + 1,
              'Stage Name': stage.name || '',
              'SNA Amount': stage.sna || '',
              'FTO Amount': stage.fto || '',
              'Cement (bags)': stage.cement || '',
              'Steel': stage.steel || '',
              'Wages': stage.wages || ''
            });
          });
        }
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 22 }, { wch: 16 }, { wch: 24 }, { wch: 12 }, { wch: 28 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 12 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Schemes');

      const today = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `masters_schemes_${today}.xlsx`);

      showSuccess(`Exported ${schemes.length} scheme${schemes.length !== 1 ? 's' : ''} to Excel`);
    } catch (err) {
      handleApiError(err, 'Failed to export schemes. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [schemes, getAuthHeaders, showError, showSuccess, clearMessages, handleApiError]);

  // Scheme selection handler
  const handleSchemeSelect = useCallback((scheme) => {
    setSelectedScheme(scheme);
    setEditingStage(null);
    clearMessages();
    setShowCreateForm(false);
    fetchSchemeDetails(scheme.name);
  }, [clearMessages, fetchSchemeDetails]);

  // Stage management for new scheme
  const handleAddStageToNewScheme = useCallback((stageData) => {
    if (!stageData.name.trim()) {
      showError('Please fill stage name');
      return;
    }

const stage = {
  name: stageData.name.trim(),
  sna: stageData.sna || '',
  fto: stageData.fto || '',
  cement: stageData.cement || '',
  steel: stageData.steel || '',
  wages: stageData.wages || '',
  order: newSchemeData.stages.length + 1
};
    setNewSchemeData(prev => ({
      ...prev,
      stages: [...prev.stages, stage]
    }));

    setNewStage(INITIAL_STAGE);
    clearMessages();
  }, [showError, clearMessages, newSchemeData.stages.length]);

  const handleRemoveStageFromNewScheme = useCallback((index) => {
    setNewSchemeData(prev => {
      const newStages = prev.stages.filter((_, i) => i !== index);
      // Reassign order numbers
      const reorderedStages = newStages.map((stage, i) => ({
        ...stage,
        order: i + 1
      }));
      return {
        ...prev,
        stages: reorderedStages
      };
    });
  }, []);

  // Create new scheme
  const handleSubmitNewScheme = useCallback(async () => {
    const { name, year, area, stages } = newSchemeData;
    
    if (!name.trim() || !year.trim() || !area.trim()) {
      showError('Please fill scheme name, year and area');
      return;
    }

    if (stages.length === 0) {
      showError('Please add at least one stage');
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        year: year.trim(),
        area: area.trim(),
        detail: stages
      };

      const response = await fetch(`${API_BASE_URL}/master/scheme`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        showSuccess('Scheme with stages created successfully');
        setNewSchemeData(INITIAL_SCHEME);
        setNewStage(INITIAL_STAGE);
        setShowCreateForm(false);
        fetchSchemes();
      } else {
        showError(data.message || 'Failed to create scheme');
      }
    } catch (err) {
      handleApiError(err, 'Network error. Please try again.');
    }
  }, [newSchemeData, showError, showSuccess, getAuthHeaders, fetchSchemes, handleApiError]);

  // Add stage to existing scheme
  const handleAddStageToExisting = useCallback(async (stageData) => {
    if (!stageData.name.trim()) {
      showError('Please fill stage name');
      return;
    }

    if (!selectedScheme) {
      showError('Please select a scheme first');
      return;
    }

    try {
      const existingStages = schemeDetails?.detail || [];
      const newOrder = existingStages.length + 1;

const newStageData = {
  name: stageData.name.trim(),
  sna: stageData.sna || '',
  fto: stageData.fto || '',
  cement: stageData.cement || '',
  steel: stageData.steel || '',
  wages: stageData.wages || '',
  order: newOrder
};

      const payload = {
        name: selectedScheme.name,
        detail: [...existingStages, newStageData],
        area: selectedScheme.area
      };

      const response = await fetch(`${API_BASE_URL}/master/scheme/${selectedScheme.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        showSuccess('Stage added successfully');
        setNewStage(INITIAL_STAGE);
        fetchSchemeDetails(selectedScheme.name);
      } else {
        showError(data.message || 'Failed to add stage');
      }
    } catch (err) {
      handleApiError(err, 'Network error. Please try again.');
    }
  }, [selectedScheme, schemeDetails, showError, showSuccess, getAuthHeaders, handleApiError, fetchSchemeDetails]);

  // Update existing stage
  const handleUpdateStage = useCallback(async (stageIndex, stageData) => {
    if (!stageData) return;

    try {
      const updatedDetail = [...schemeDetails.detail];
updatedDetail[stageIndex] = {
  ...updatedDetail[stageIndex],
  name: stageData.name.trim(),
  sna: stageData.sna || '',
  fto: stageData.fto || '',
  cement: stageData.cement || '',
  steel: stageData.steel || '',
  wages: stageData.wages || ''
};
      const payload = {
        name: selectedScheme.name,
        detail: updatedDetail,
        area: selectedScheme.area
      };

      const response = await fetch(`${API_BASE_URL}/master/scheme/${selectedScheme.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        showSuccess('Stage updated successfully');
        setEditingStage(null);
        fetchSchemeDetails(selectedScheme.name);
      } else {
        showError(data.message || 'Failed to update stage');
      }
    } catch (err) {
      handleApiError(err, 'Network error. Please try again.');
    }
  }, [selectedScheme, schemeDetails, showSuccess, showError, getAuthHeaders, handleApiError, fetchSchemeDetails]);

  // Update stage order after drag and drop
  const updateStageOrder = useCallback(async (reorderedStages) => {
    try {
      // Add order numbers to stages
      const stagesWithOrder = reorderedStages.map((stage, index) => ({
        ...stage,
        order: index + 1
      }));

      const payload = {
        name: selectedScheme.name,
        detail: stagesWithOrder,
        area: selectedScheme.area
      };

      const response = await fetch(`${API_BASE_URL}/master/scheme/${selectedScheme.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        showSuccess('Stage order updated successfully');
        fetchSchemeDetails(selectedScheme.name);
      } else {
        showError(data.message || 'Failed to update stage order');
      }
    } catch (err) {
      handleApiError(err, 'Network error. Please try again.');
    }
  }, [selectedScheme, getAuthHeaders, showSuccess, showError, handleApiError, fetchSchemeDetails]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, stageIndex) => {
    setDraggedStage(stageIndex);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    
    if (draggedStage === null || draggedStage === dropIndex) {
      setDraggedStage(null);
      setDragOverIndex(null);
      return;
    }

    const stages = [...schemeDetails.detail];
    const draggedItem = stages[draggedStage];
    
    // Remove dragged item
    stages.splice(draggedStage, 1);
    
    // Insert at new position
    const insertIndex = draggedStage < dropIndex ? dropIndex - 1 : dropIndex;
    stages.splice(insertIndex, 0, draggedItem);

    // Update state immediately for UI feedback
    setSchemeDetails(prev => ({
      ...prev,
      detail: stages
    }));

    // Update order in backend
    updateStageOrder(stages);

    setDraggedStage(null);
    setDragOverIndex(null);
  }, [draggedStage, schemeDetails, updateStageOrder]);

  const handleDragEnd = useCallback(() => {
    setDraggedStage(null);
    setDragOverIndex(null);
  }, []);

  // Delete handlers
  const handleDeleteStage = useCallback((stageIndex) => {
    setConfirmDelete({ type: 'stage', index: stageIndex });
  }, []);

  const handleDeleteScheme = useCallback((schemeId) => {
    setConfirmDelete({ type: 'scheme', id: schemeId });
  }, []);

  const confirmDeleteAction = useCallback(async () => {
    if (!confirmDelete) return;

    try {
      let response;
      
      if (confirmDelete.type === 'stage') {
        const updatedDetail = schemeDetails.detail.filter((_, index) => index !== confirmDelete.index);
        // Reassign order numbers
        const reorderedDetail = updatedDetail.map((stage, index) => ({
          ...stage,
          order: index + 1
        }));
        
        const payload = {
          name: selectedScheme.name,
          detail: reorderedDetail,
          area: selectedScheme.area
        };

        response = await fetch(`${API_BASE_URL}/master/scheme/${selectedScheme.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
      } else if (confirmDelete.type === 'scheme') {
        const payload = {
          name: selectedScheme.name,
          detail: selectedScheme.detail,
          area: selectedScheme.area,
          status: 'delete'
        };
        
        response = await fetch(`${API_BASE_URL}/master/scheme/${confirmDelete.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (response.ok) {
        showSuccess(`${confirmDelete.type === 'stage' ? 'Stage' : 'Scheme'} deleted successfully`);
        
        if (confirmDelete.type === 'stage') {
          fetchSchemeDetails(selectedScheme.name);
        } else {
          setSelectedScheme(null);
          setSchemeDetails(null);
          fetchSchemes();
        }
      } else {
        showError(data.message || `Failed to delete ${confirmDelete.type}`);
      }
    } catch (err) {
      handleApiError(err, 'Network error. Please try again.');
    } finally {
      setConfirmDelete(null);
    }
  }, [confirmDelete, selectedScheme, schemeDetails, showSuccess, showError, getAuthHeaders, handleApiError, fetchSchemes, fetchSchemeDetails]);

  // Edit stage handler
  const startEditStage = useCallback((stage, index) => {
    setEditingStage({ ...stage, index });
    clearMessages();
  }, [clearMessages]);

  // Form handlers
  const handleShowCreateForm = useCallback(() => {
    setShowCreateForm(true);
    setSelectedScheme(null);
    setSchemeDetails(null);
    setNewSchemeData(INITIAL_SCHEME);
    setNewStage(INITIAL_STAGE);
    clearMessages();
  }, [clearMessages]);

  const handleCancelCreateForm = useCallback(() => {
    setShowCreateForm(false);
    setNewSchemeData(INITIAL_SCHEME);
    setNewStage(INITIAL_STAGE);
  }, []);

  // Stage change handlers
  const handleNewStageChange = useCallback((field, value) => {
    setNewStage(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditingStageChange = useCallback((field, value) => {
    setEditingStage(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSchemeDataChange = useCallback((field, value) => {
    setNewSchemeData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Utility functions
  const formatCurrency = useCallback((amount) => {
    if (!amount) return 'N/A';
    return `₹${parseInt(amount).toLocaleString()}`;
  }, []);

  const getStageDisplayValue = useCallback((value, suffix = '') => {
    if (!value) return 'N/A';
    return suffix ? `${value} ${suffix}` : value;
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
          
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <Settings className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Masters Management</h1>
                  <p className="text-gray-600">Manage housing schemes, stages, payments and materials</p>
                </div>
              </div>
              <button
                onClick={handleExportToExcel}
                disabled={exporting || schemes.length === 0}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileSpreadsheet className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Panel - Schemes List */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Housing Schemes
                </h2>
                <button
                  onClick={handleShowCreateForm}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Create New
                </button>
              </div>

              {loading && schemes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  Loading schemes...
                </div>
              ) : schemes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No schemes found</p>
                  <p className="text-sm">Click "Create New" to add one</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {schemes.map((scheme) => (
                    <div
                      key={scheme.id}
                      onClick={() => handleSchemeSelect(scheme)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedScheme?.id === scheme.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{scheme.name}</div>
                  <div className="text-sm text-gray-600">
                        Year: {scheme.year || 'N/A'} | Allotted Size: {scheme.area || 'N/A'} sq ft
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {scheme.detail && scheme.detail.length > 0 ? `${scheme.detail.length} stages configured` : 'No stages configured'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Right Panel */}
            <div className="lg:col-span-2 space-y-6">
              
              {showCreateForm ? (
                /* Create New Scheme Form */
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      Create New Scheme
                    </h2>
                    <button
                      onClick={handleCancelCreateForm}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

{/* Scheme Basic Information */}
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Scheme Name *</label>
                        <input
                          type="text"
                          value={newSchemeData.name}
                          onChange={(e) => handleSchemeDataChange('name', e.target.value)}
                          placeholder="e.g., PM-JANMAN"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Financial Year *</label>
                        <input
                          type="text"
                          value={newSchemeData.year}
                          onChange={(e) => handleSchemeDataChange('year', e.target.value)}
                          placeholder="2023-2024"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Allotted House Size in Sq Ft *</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={newSchemeData.area}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            handleSchemeDataChange('area', value);
                          }}
                          placeholder="360"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Added Stages List */}
                  {newSchemeData.stages.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-md font-semibold text-gray-900 mb-3">Added Stages ({newSchemeData.stages.length})</h3>
                      <div className="space-y-2">
                        {newSchemeData.stages.map((stage, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">{stage.name}</div>
<div className="text-sm text-gray-600">
  SNA: {formatCurrency(stage.sna)} | 
  FTO: {formatCurrency(stage.fto)} | 
  Cement: {getStageDisplayValue(stage.cement, 'bags')} | 
  Steel: {getStageDisplayValue(stage.steel)} | 
  Wages: {getStageDisplayValue(stage.wages)}
</div>
                            </div>
                            <button
                              onClick={() => handleRemoveStageFromNewScheme(index)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Stage Form */}
                  <div className="border-t pt-4">
                    <h3 className="text-md font-semibold text-gray-900 mb-4">Add Construction Stage</h3>
                    <StageForm 
                      stage={newStage}
                      onSubmit={handleAddStageToNewScheme}
                      onStageChange={handleNewStageChange}
                    />
                    
                    {newSchemeData.stages.length > 0 && (
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={handleSubmitNewScheme}
                          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                        >
                          <Save className="w-4 h-4" />
                          Create Scheme with {newSchemeData.stages.length} Stage{newSchemeData.stages.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : selectedScheme ? (
                <>
                  {/* Existing Scheme Details */}
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Info className="w-5 h-5" />
                        Scheme: {selectedScheme.name}
                      </h2>
                      <button
                        onClick={() => handleDeleteScheme(selectedScheme.id)}
                        className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Scheme
                      </button>
                    </div>

{schemeDetails && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Scheme Name</label>
                          <div className="text-lg font-semibold text-gray-900">{schemeDetails.name}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Financial Year</label>
                          <div className="text-lg font-semibold text-gray-900">{schemeDetails.year || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Allotted House Size in Sq Ft</label>
                          <div className="text-lg font-semibold text-gray-900">{schemeDetails.area ? `${schemeDetails.area} sq ft` : 'N/A'}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Stages Management */}
                  <div className="bg-white rounded-lg shadow-sm border p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Package className="w-5 h-5" />
                        Construction Stages
                      </h3>
                      <div className="text-sm text-gray-500">
                        Drag stages to reorder
                      </div>
                    </div>

                    {loading && selectedScheme ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        Loading scheme details...
                      </div>
                    ) : schemeDetails && schemeDetails.detail && schemeDetails.detail.length > 0 ? (
                      <div className="space-y-4">
                        {schemeDetails.detail.map((stage, index) => (
                          <div 
                            key={index} 
                            draggable={!editingStage || editingStage.index !== index}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`border border-gray-200 rounded-lg p-4 transition-all ${
                              draggedStage === index ? 'opacity-50' : ''
                            } ${
                              dragOverIndex === index ? 'border-blue-500 bg-blue-50' : ''
                            } ${
                              (!editingStage || editingStage.index !== index) ? 'cursor-move hover:shadow-md' : ''
                            }`}
                          >
                            {editingStage && editingStage.index === index ? (
                              <StageForm 
                                stage={editingStage}
                                onSubmit={(stageData) => handleUpdateStage(index, stageData)}
                                onCancel={() => setEditingStage(null)}
                                isEditing={true}
                                onStageChange={handleEditingStageChange}
                              />
                            ) : (
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <GripVertical className="w-5 h-5 text-gray-400" />
                                    <h4 className="text-lg font-semibold text-gray-900">{stage.name}</h4>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => startEditStage(stage, index)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteStage(index)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
<div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
  <div>
    <label className="font-medium text-gray-600">SNA AMOUNT</label>
    <div className="text-gray-900">{formatCurrency(stage.sna)}</div>
  </div>
  <div>
    <label className="font-medium text-gray-600">FTO AMOUNT</label>
    <div className="text-gray-900">{formatCurrency(stage.fto)}</div>
  </div>
  <div>
    <label className="font-medium text-gray-600">Cement</label>
    <div className="text-gray-900">{getStageDisplayValue(stage.cement, 'bags')}</div>
  </div>
  <div>
    <label className="font-medium text-gray-600">Steel</label>
    <div className="text-gray-900">{getStageDisplayValue(stage.steel)}</div>
  </div>
  <div>
    <label className="font-medium text-gray-600">Wages</label>
    <div className="text-gray-900">{getStageDisplayValue(stage.wages)}</div>
  </div>
</div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No stages configured</p>
                        <p className="text-sm">Add construction stages below</p>
                      </div>
                    )}

                    {/* Add New Stage Form for Existing Scheme */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">Add New Stage</h4>
                      <StageForm 
                        stage={newStage}
                        onSubmit={handleAddStageToExisting}
                        onStageChange={handleNewStageChange}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Welcome to Masters Management</h3>
                  <p className="text-gray-600 mb-4">Create a new scheme or select an existing one to get started</p>
                  <button
                    onClick={handleShowCreateForm}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                    Create New Scheme
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
              <button
                onClick={() => setConfirmDelete(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-600">
                {confirmDelete.type === 'stage' 
                  ? 'Are you sure you want to delete this stage?' 
                  : 'Are you sure you want to delete this scheme? This action cannot be undone.'
                }
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAction}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MastersManagement;